export default {
  index: {
    title: 'Gidsen over getijden en stromingen',
    description:
      'Ontdek hoe getijden en getijstromen werken: getijcoëfficiënten, vloed- en ebstromen, getijreferentievlakken en hoe getijvoorspellingen worden berekend.',
    subtitle:
      'Korte, begrijpelijke uitleg over de wetenschap achter getij- en stromingsvoorspellingen.',
  },
  back: 'Terug naar alle gidsen',
  guides: {
    'tidal-coefficients': {
      title: 'Getijcoëfficiënten uitgelegd',
      description:
        'Wat getijcoëfficiënten betekenen: de schaal van 20 tot 120, hoe springtij en doodtij samenhangen met de maan, en waarom een hogere coëfficiënt een groter getijverschil oplevert.',
      blurb:
        'De schaal van 20 tot 120 die samenvat hoe groot een getij wordt, en hoe de maan het aandrijft.',
      body: [
        {
          p: 'Een getijcoëfficiënt is één getal dat samenvat hoe groot een getij op een bepaalde dag zal zijn. Hij wordt veel gebruikt in Frankrijk en andere landen die de Franse hydrografische traditie volgen. De coëfficiënt is dimensieloos en loopt normaal gesproken van ongeveer 20 tot 120: hoe groter het getal, hoe groter het verschil tussen hoog- en laagwater, en hoe sterker de bijbehorende getijstromen.',
        },
        { h2: 'De schaal van 20 tot 120' },
        {
          p: 'De coëfficiënt vergelijkt het getijverschil van de dag met een langjarige referentie. Verschillende punten op de schaal hebben een gebruikelijke betekenis:',
        },
        {
          list: [
            {
              term: '20',
              text: 'de kleinste getijden, dicht bij het minimale astronomische getijverschil.',
            },
            { term: '45', text: 'een gemiddeld doodtij, wanneer het getijverschil klein is.' },
            {
              term: '70',
              text: 'een gemiddeld getij, ongeveer het midden tussen dood- en springtij.',
            },
            { term: '95', text: 'een gemiddeld springtij, wanneer het getijverschil groot is.' },
            {
              term: '120',
              text: 'de grootste getijden, dicht bij het maximale astronomische getijverschil.',
            },
          ],
        },
        { h2: 'Waarom hij verandert: de maan' },
        {
          p: 'Coëfficiënten stijgen en dalen in de cyclus van springtij en doodtij van ongeveer twee weken. Springtij, met hoge coëfficiënten, treedt een dag of twee na elke nieuwe en volle maan op, wanneer de zon en de maan op één lijn staan en hun getijkrachten elkaar versterken. Doodtij, met lage coëfficiënten, treedt op rond het eerste en laatste kwartier, wanneer de zon en de maan een rechte hoek maken en elkaar deels opheffen.',
        },
        {
          p: 'Ook de afstand van de maan speelt een rol. Wanneer een springtij samenvalt met het perigeum (de kleinste afstand van de maan tot de aarde), bereikt de coëfficiënt zijn hoogste waarden. De allergrootste getijden treden meestal geclusterd op rond de lente- en herfstnachtevening.',
        },
        { h2: 'Wat een coëfficiënt je niet vertelt' },
        {
          p: 'De coëfficiënt beschrijft alleen het astronomische getijverschil. Hij houdt geen rekening met de invloed van het weer: wind en luchtdruk kunnen het werkelijke waterpeil verhogen of verlagen, ongeacht de coëfficiënt. Bovendien is het een eigenschap van het getij als geheel voor die dag, geen hoogte in meters of voet op een specifieke locatie.',
        },
      ],
    },
    'tidal-currents': {
      title: 'Vloed, eb en kentering: hoe getijstromen werken',
      description:
        'Hoe getijstromen werken: de richting van vloed en eb, kentering bij het keren, stroomsnelheid in knopen en de stroomrichting, en waarom kentering niet altijd samenvalt met hoog- en laagwater.',
      blurb:
        'Vloed, eb en kentering, en waarom de sterkste stroom niet altijd bij hoogwater optreedt.',
      body: [
        {
          p: 'Getijstromen zijn de horizontale waterbeweging die de verticale rijzing en daling van het getij begeleidt. Waar de getijhoogte aangeeft hoe diep het water is, vertelt de stroom welke kant het op stroomt en hoe snel. Stromen zijn het belangrijkst in geulen, zeegaten en estuaria, waar ze meerdere knopen kunnen halen.',
        },
        { h2: 'Vloed, eb en kentering' },
        {
          list: [
            {
              term: 'Vloedstroom',
              text: 'water dat landinwaarts of een estuarium op stroomt, doorgaans terwijl het getij stijgt.',
            },
            {
              term: 'Ebstroom',
              text: 'water dat zeewaarts of een estuarium af stroomt, doorgaans terwijl het getij daalt.',
            },
            {
              term: 'Kentering',
              text: 'de korte pauze waarin de stroom bijna nul is terwijl hij keert tussen vloed en eb.',
            },
          ],
        },
        {
          p: 'De stroomsnelheid wordt gemeten in knopen (zeemijlen per uur). De richting, de stroomrichting genoemd, wordt aangegeven als een kompaspeiling: de richting waarheen het water stroomt.',
        },
        { h2: 'Kentering valt niet altijd samen met hoogwater' },
        {
          p: 'Een veelvoorkomende aanname is dat de stroom stopt bij hoog- en laagwater. Dat geldt maar op sommige plaatsen. De timing hangt af van hoe de getijgolf zich plaatselijk gedraagt. Waar het getij zich als een voortschrijdende golf gedraagt, zoals in een lange open geul, treden de sterkste vloed en eb op rond hoog- en laagwater, en valt de kentering rond het midden. Waar het zich als een staande golf gedraagt, zoals aan het eind van een gesloten baai, is het omgekeerd: de snelste stroom treedt op rond halftij en de kentering valt rond hoog- en laagwater. Plaatselijke stroomtabellen zijn de betrouwbare gids.',
        },
        { h2: 'Omkerende en roterende stromen' },
        {
          p: 'In nauwe geulen is de stroom meestal omkerend: hij loopt bij vloed de ene kant op, keert dan en loopt bij eb terug. In open kustwater is de stroom vaak juist roterend en verandert hij over een getijcyclus voortdurend van richting rond het kompas, in plaats van langs één lijn om te keren.',
        },
      ],
    },
    'tidal-datums': {
      title: 'Getijreferentievlakken en getijhoogten',
      description:
        'Waar een getijhoogte vanaf wordt gemeten: reductievlak, laagst astronomisch getij (LAT), gemiddeld lager laagwater (MLLW), en waarom referentievlakken tussen landen verschillen.',
      blurb: 'Reductievlak, LAT, MLLW en wat het getal in een getijhoogte werkelijk betekent.',
      body: [
        {
          p: 'Elke getijhoogte is een meting boven een referentieniveau dat een referentievlak wordt genoemd. Als een getijtabel zegt dat het water 1.8 meter zal bereiken, betekent dat 1.8 meter boven het plaatselijke getijreferentievlak: niet boven de zeebodem en niet boven de zeespiegel in de alledaagse zin. Kennis van het referentievlak is wat een hoogte betekenisvol maakt.',
        },
        { h2: 'Wat een getijreferentievlak is' },
        {
          p: 'Een getijreferentievlak is een referentiehoogte die wordt bepaald door een bepaald stadium van het getij, gemiddeld over een lange periode: gebruikelijk ongeveer 19 jaar, wat de cyclus van 18.6 jaar in de baan van de maan uitmiddelt. Omdat het een gemiddelde is van echte waarnemingen, is een referentievlak specifiek voor een locatie en voor de instantie die het heeft berekend.',
        },
        { h2: 'Reductievlak en gangbare referenties' },
        {
          p: 'Zeekaarten gebruiken een laagwaterreferentievlak, zodat de werkelijke diepte vrijwel altijd minstens de gekarteerde diepte is. Dit reductievlak is het nulpunt van waaruit zowel de gekarteerde diepten als de getijhoogten worden gerekend. Verschillende landen hanteren verschillende referentievlakken:',
        },
        {
          list: [
            {
              term: 'LAT (laagst astronomisch getij)',
              text: 'het laagste niveau dat onder gemiddelde weersomstandigheden voorspelbaar is; gebruikt door het Verenigd Koninkrijk en vele andere landen.',
            },
            {
              term: 'MLLW (gemiddeld lager laagwater)',
              text: 'het gemiddelde van het laagste van de twee dagelijkse laagwaters; het standaardreductievlak in de Verenigde Staten.',
            },
            {
              term: 'MSL (gemiddelde zeespiegel)',
              text: 'het langjarig gemiddelde waterpeil, vooral gebruikt als referentie voor landhoogten in plaats van voor zeekaarten.',
            },
          ],
        },
        {
          p: 'Hoogwaterreferentievlakken zoals gemiddeld hoogwater (MHW) en gemiddeld hoger hoogwater (MHHW) worden voor het tegenovergestelde doel gebruikt, bijvoorbeeld om een kustlijn of een doorvaarthoogte onder een brug te bepalen.',
        },
        { h2: 'Waarom hoogten niet direct vergelijkbaar zijn' },
        {
          p: 'Omdat referentievlakken verschillen, kan een hoogte bij het ene station niet rechtstreeks worden vergeleken met een hoogte bij een ander, tenzij beide dezelfde referentie gebruiken. Een laagwater van 0.5 meter gemeten vanaf LAT is niet hetzelfde waterpeil als een laagwater van 0.5 meter gemeten vanaf MLLW. Dit is ook de reden waarom hetzelfde fysieke getij in de tabellen van verschillende landen door verschillende getallen kan worden beschreven.',
        },
      ],
    },
    'how-tide-predictions-work': {
      title: 'Hoe getijvoorspellingen worden berekend',
      description:
        'Hoe getijden worden voorspeld: de zwaartekracht van de maan en de zon, harmonische componenten zoals M2 en S2, en waarom waargenomen waterpeilen afwijken van de astronomische voorspelling.',
      blurb:
        'Harmonische analyse, de componenten achter het getij, en waarom het weer de werkelijkheid doet afwijken.',
      body: [
        {
          p: 'Getijden kunnen jaren van tevoren worden voorspeld omdat ze worden aangedreven door de bewegingen van de maan en de zon, die met grote precisie bekend zijn. Het getij op een plaats is de reactie van de oceaan op die zwaartekracht, gevormd door de plaatselijke kustlijn en diepte. De methode die astronomie omzet in een getijtabel heet harmonische analyse.',
        },
        { h2: 'Het getij als som van componenten' },
        {
          p: 'De harmonische analyse beschouwt het getij als de som van vele eenvoudige golven, elk een component genoemd. Elke component heeft een frequentie die door de astronomie is vastgelegd (door hoe de aarde, de maan en de zon ten opzichte van elkaar bewegen) terwijl de amplitude en timing worden gevonden door de componenten aan te passen aan een periode van echte waterstandswaarnemingen op die locatie. De belangrijkste componenten zijn onder meer:',
        },
        {
          list: [
            {
              term: 'M2',
              text: 'de belangrijkste halfdaagse maancomponent, periode 12.42 uur; meestal de grootste.',
            },
            { term: 'S2', text: 'de belangrijkste halfdaagse zoncomponent, periode 12.00 uur.' },
            {
              term: 'N2',
              text: 'een maancomponent afkomstig van de elliptische baan van de maan.',
            },
            {
              term: 'K1 en O1',
              text: 'de belangrijkste daagse componenten, die op veel plaatsen het ene hoogwater per dag groter maken dan het andere.',
            },
          ],
        },
        {
          p: 'De bekende cyclus van springtij en doodtij komt hier vanzelf uit voort: M2 en S2 hebben iets verschillende perioden, dus ze raken in en uit de pas: om de 14.8 dagen versterken ze elkaar tot springtij en heffen ze elkaar deels op tot doodtij.',
        },
        { h2: 'Vooruit voorspellen' },
        {
          p: 'Zodra de amplitude en fase van elke component voor een locatie bekend zijn, is het voorspellen van het getij niet meer dan het berekenen van de som op een willekeurig toekomstig tijdstip. De astronomische frequenties veranderen nooit, dus de berekening is deterministisch en kan tientallen jaren vooruit lopen.',
        },
        { h2: 'Waarom het werkelijke getij afwijkt' },
        {
          p: 'Een voorspelling is alleen het astronomische getij. Ze houdt geen rekening met het weer. Harde wind, lage of hoge luchtdruk, hevige regenval en rivierafvoer kunnen het werkelijke waterpeil verhogen of verlagen: een stormvloed kan een meter of meer boven de voorspelling toevoegen, terwijl hoge druk en aflandige wind het water er ruim onder kunnen houden. Voorspellingen zijn daarom een uitstekende leidraad voor het tijdstip en de grootte van het getij, maar het waargenomen peil op de dag zelf kan afwijken.',
        },
      ],
    },
  },
}
